import { trigger, transition, style, animate } from '@angular/animations';
import { Component, HostListener, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Router } from '@angular/router';
import { KerkimiService } from '@core/services/kerkimi.service';
import { OperatoriFaqjaService } from '@core/services/operatori-faqja.service';
import { ShtoFjalenShpejtDialogComponent } from '@home/dialogs/shto-fjalen-shpejt-dialog/shto-fjalen-shpejt-dialog.component';
import { combineLatest, debounceTime, distinctUntilChanged, filter, map, pairwise, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { ConfirmWordChangeDialogComponent } from 'src/app/shared/dialogs/confirm-word-change-dialog/confirm-word-change-dialog.component';
import { ConfirmWordChangeWithoutSaveDialogComponent } from 'src/app/shared/dialogs/confirm-word-change-without-save-dialog/confirm-word-change-without-save-dialog.component';
import { AuthService } from '../auth/auth.service';
import { EditorService } from '../services/editor.service';
import { NavigationService } from '../services/navigation.service';
import { ShenimetPopupComponent } from './shenimet-popup/shenimet-popup.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  animations: [
    trigger('headerAnim', [
      transition(':enter', [style({ transform: 'translateY(-100%)' }), animate('0.3s ease-in-out', style({ transform: 'translateY(0)' }))]),
      transition(':leave', [style({ transform: 'translateY(0)' }), animate('0.3s ease-in-out', style({ transform: 'translateY(-100%)' }))]),
    ]),
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  isLoggedIn: boolean = false;
  canAdministrate: boolean = false;

  showNavigationButtons: boolean = false;

  formDirty: boolean = false;
  formValid: boolean = false;

  prevFjalaId: number | null = null;
  nextFjalaId: number | null = null;

  canDelete: boolean = false;

  canUndo: boolean = false;
  canRedo: boolean = false;

  saving: boolean = false;
  loading: boolean = false;

  jumpWordID: number | null;

  darkMode: boolean = false;

  private _onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    public dialog: MatDialog,
    private authService: AuthService,
    private editorService: EditorService,
    private navigationService: NavigationService,
    private operatoriFaqjaService: OperatoriFaqjaService,
    private kerkimiService: KerkimiService,
    private matDialog: MatDialog,
    private router: Router,
    private renderer: Renderer2
  ) {
    // router.events
    //   .pipe(
    //     filter((event) => event instanceof NavigationEnd),
    //     takeUntil(this._onDestroy$)
    //   )
    //   .subscribe((event: any) => {
    //     this.showNavigationButtons = false;

    //     if (event.url === '/home' || event.urlAfterRedirects === '/home') {
    //       this.showNavigationButtons = true;
    //     }
    //   });

    this.showNavigationButtons = true;
  }

  ngOnInit(): void {
    this.authService.isDoneLoading$
      .pipe(
        filter((isDone) => isDone),
        switchMap((_) => this.authService.isAuthenticated$),
        map((isAuthenticated) => {
          if (isAuthenticated && this.authService.hasLinguistPermission()) {
            return true;
          }
          return false;
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;
      });

    combineLatest([this.editorService.form.get('id')!.valueChanges.pipe(distinctUntilChanged()), this.editorService.getSaving()])
      .pipe(
        debounceTime(0),
        map(([bazaId, saving]) => {
          return bazaId != null && !saving;
        }),
        distinctUntilChanged(),
        takeUntil(this._onDestroy$)
      )
      .subscribe((canDelete) => {
        this.canDelete = canDelete;
      });

    combineLatest([this.editorService.form.valueChanges, this.editorService.form.statusChanges])
      .pipe(debounceTime(0), takeUntil(this._onDestroy$))
      .subscribe(() => {
        this.formValid = this.editorService.form.valid;
        this.formDirty = this.editorService.form.dirty;


        this.editorService.form.get('dialektiId')?.setValue(10);
        this.editorService.form.get('homonim')?.setValue(0);
      });

    this.editorService
      .canUndo()
      .pipe(
        // debounceTime(0),
        distinctUntilChanged(),
        takeUntil(this._onDestroy$)
      )
      .subscribe((canUndo) => {
        this.canUndo = canUndo;
      });

    this.editorService
      .canRedo()
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((canRedo) => {
        this.canRedo = canRedo;
      });

    this.editorService
      .getSaving()
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((saving) => {
        this.saving = saving;
      });

    this.editorService
      .getLoading()
      .pipe(debounceTime(0), takeUntil(this._onDestroy$))
      .subscribe((loading) => {
        this.loading = loading;
      });

    this.navigationService
      .getPrevFjala()
      .pipe(debounceTime(0), takeUntil(this._onDestroy$))
      .subscribe((id) => {
        this.prevFjalaId = id;
      });

    this.navigationService
      .getNextFjala()
      .pipe(debounceTime(0), takeUntil(this._onDestroy$))
      .subscribe((id) => {
        this.nextFjalaId = id;
      });

    this.kerkimiService
      .getJumpWordID()
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((wordID) => {
        this.jumpWordID = wordID;
      });

    this.operatoriFaqjaService
      .listenOnFieldChanges('darkMode')
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((darkMode) => {
        if (darkMode) {
          this.renderer.addClass(document.body, 'dark-theme');
          this.renderer.removeClass(document.body, 'light-theme');
        } else {
          this.renderer.addClass(document.body, 'light-theme');
          this.renderer.removeClass(document.body, 'dark-theme');
        }
        this.darkMode = darkMode;
        localStorage.setItem('darkMode', darkMode);
      });

    // this.operatoriFaqjaService.listenOnFieldChanges('bazaId').pipe(
    //   pairwise(),
    //   takeUntil(this._onDestroy$)
    // ).subscribe(([prev, next]) => {
    //   if(prev != null && next != null) {
    //     this.jumpWordID = prev;
    //   } else {
    //     this.jumpWordID = null;
    //   }
    // });
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if ((event.metaKey || event.ctrlKey) && event.key === 's') {
      this.onSaveNextClick();
      event.preventDefault();
    }
  }

  saveWord() {
    if (this.formDirty && this.formValid) this.navigationService.saveWord();
  }

  undo() {
    if (this.canUndo) this.editorService.undo();
  }

  redo() {
    if (this.canRedo) this.editorService.redo();
  }

  onEditorClearClick() {
    if (!this.saving) this.navigationService.triggerOnEditorClearClick();
  }

  ondeleteClick() {
    this.navigationService.triggerDeleteClick();
  }

  onPrevClick() {
    this.navigationService.triggerPrevClick();
  }

  onNextClick() {
    this.navigationService.triggerNextClick();
  }

  onSaveBackClick() {
    this.navigationService.triggerSaveBackClick();
  }

  onSaveNextClick() {
    this.navigationService.triggerSaveNextClick();
  }

  addQuickWord() {
    let dialog = this.matDialog.open(ShtoFjalenShpejtDialogComponent, {
      width: '400px',
      disableClose: true,
    });

    dialog.afterClosed().subscribe((response) => {
      if (response) {
        console.log('word added');
      }
    });
  }

  onCommentsClick() {
    this.navigationService.triggerCommentsClick();
  }

  toggleTheme() {
    this.operatoriFaqjaService.updateItems({ darkMode: !this.darkMode });
  }

  logout() {
    this.authService.logout();
  }

  openShenimet() {
    this.matDialog.open(ShenimetPopupComponent, {
      disableClose: true,
      panelClass: 'big-resizable-dialog',
    });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
